import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { Signage } from '../../Signage';
import { Log } from '../../AuxillaryService/Log';
import { TemplateWidgetMixin } from '../../Template';
import { DateTime } from 'luxon';

import {
    WeatherWidgetConfigurationDetails,
    WeatherWidgetConfiguration,
    ZodWeatherWidgetConfigurationDetails,
    WeatherDataResponse,
    WeatherWidgetTemplateData,
} from './types';
import { WidgetType } from '../types';
import { WeatherData } from './WeatherData';
import { isNullish } from '../../Utility/Nullish';
import { WeatherLayoutType } from '../WeatherWidget/types';
import { getTemperatureScale } from '../../Signage/urlOverrides';
import { pushReceiver } from '../../MessagingClient/instance';
import { getWeatherLocation } from '../../ReachApiV2/endpoints';
import { moment } from '../../../vendor/moment';

/**
 * Weather Widget
 * @mixes TemplateWidgetMixin
 */
export class WeatherWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.WEATHER = WidgetType.WEATHER;
    declare readonly config: WeatherWidgetConfigurationDetails;

    schedulerRunInterval = 2820;
    lastUpdate: number | undefined;
    weatherData: WeatherData | undefined;

    constructor(widgetConfiguration: WeatherWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.lastUpdate = undefined;
    }

    protected validateConfigurationDetails(
        maybeWeatherWidgetConfigurationDetails: WeatherWidgetConfigurationDetails
    ): WeatherWidgetConfigurationDetails {
        return ZodWeatherWidgetConfigurationDetails.parse(maybeWeatherWidgetConfigurationDetails);
    }

    async initialize(): Promise<void> {
        await super.initialize();

        pushReceiver().addListener('weather-update', (event) => {
            if (event.detail.location === this.config.zip) {
                Log.Info(
                    `Received push notification from MessagingClient for weather location "${event.detail.location}"`,
                    null,
                    __fileline
                );

                this._updateComponentWithApiData();
            }
        });

        pushReceiver()
            .subscribeToWeather(this.config.zip)
            .then(() => {
                Log.Debug(
                    `Weather Widget Subscribed. widget id=${this.id}, zip=${this.config.zip}`,
                    null,
                    __fileline
                );
            })
            .catch((error: unknown) => {
                //failure!
                Log.Error(
                    `Failed to subscribe to channel ${this.config.zip}`,
                    null,
                    __fileline,
                    error
                );
            });
    }

    /**
     * @inheritdoc
     * Updates weather data and renders the widget
     */
    async getReadyForDisplay(): Promise<void> {
        await this._updateComponentWithApiData();
    }

    /**
     * Get the weather icon based on icon and version
     */
    static getWeatherIcon(icon: string, version: string | null): string {
        return `/images/weather_icon/${version ? version + '/' : ''}${icon}.png`;
    }

    /**
     * Get the weather background based on condition and version
     */
    static getWeatherBackground(condition: string): string {
        return `/images/weather_background/v1/${condition}.png`;
    }

    /**
     * Get the weather day name from a unix timestamp in seconds
     */
    static dayNameFromUnixTimestamp(timestamp: number): string {
        const date = DateTime.fromSeconds(timestamp);
        date.setZone(Signage.localTimeZone);
        return date.toLocaleString({ weekday: 'short' });
    }

    /**
     * Updates and parses all the weather data
     * (see API documentation for endpoint [/weather]
     */
    _updateComponent(json: WeatherDataResponse): void {
        const temperatureUnits =
            getTemperatureScale(window.location.href) || this.config.temperature_scale || 'f';

        //always use the bang-bang when possible
        this.hasContentToPlay = !!this.config.zip && json ? true : false;

        this.lastUpdate = Date.now();

        if (!this.hasContentToPlay) {
            return;
        }

        this.weatherData = new WeatherData(
            json.weather,
            temperatureUnits,
            this.config.icon_version
        );

        this.renderContent().catch((error: Error) => {
            Log.Error(
                `Failed to render widget content (${this.id} : ${this.name})`,
                null,
                __fileline,
                error
            );
        });
    }

    /**
     * Generate the data object which is used to build templates
     * NOTE -- Two extra properties are added for the template stories data
     */
    protected getRenderContext(): WeatherWidgetTemplateData {
        // /**
        //  * In the DB model it uses `wobj.widget.widgetsid`, which is depreciated
        //  *   and instead should look at using `Widget.Id`.
        //  */
        // wobj: {
        //     widget: {
        //         widgetsid: this.id,
        //     },
        //     config: {
        //         clock_type: this.config.clock_type,
        //     },
        // },
        return {
            wid: this.id.toString(),

            wobj: {
                widget: {
                    widgetsid: this.id,
                    config: {
                        css: this.config.css,
                        css_day: this.config.css_day,
                        css_temp: this.config.css_temp,
                        css_icon: this.config.css_icon,
                    },
                },
            },

            Widget: { Id: this.id },
            widget: {
                id: this.id,
            },
            weather: this.weatherData?.weather || null,
            hourly: this.weatherData?.hourly || [],
            lastUpdated: this.lastUpdate,
            landscape: this.getLayoutType() === WeatherLayoutType.LANDSCAPE,
            portrait: this.getLayoutType() === WeatherLayoutType.PORTRAIT,
            metadata: {
                landscape: this.getLayoutType() === WeatherLayoutType.LANDSCAPE,
                fontResize: this.getLayoutType() === WeatherLayoutType.LANDSCAPE,
                fontProperties: {
                    checkAllChildren: false,
                    checkWidthOverflow: false,
                    ellipsis: false,
                    maxSize: this.getMaximumFontSize(),
                    maxTextHeight: 0,
                    minSize: this.getMinimumFontSize(),
                },
            },
        };
    }

    /**
     * Update with weather data from the API
     */
    async _updateComponentWithApiData(): Promise<void> {
        getWeatherLocation(this.widget.config.zip).then(
            (response) => {
                this._updateComponent({
                    ...response,
                    weather: {
                        ...response.weather,
                        forecast: response.weather.forecast.map((forecast) => {
                            return {
                                ...forecast,
                                date: moment
                                    .utc(forecast.date)
                                    .tz(Signage.localTimeZone)
                                    .format('MM/DD'),
                            };
                        }),
                        hourly: response.weather.hourly.map((hourlyForecast) => {
                            const utcMoment = moment.utc(hourlyForecast.date);

                            return {
                                ...hourlyForecast,
                                utc: utcMoment.format(),
                                time: utcMoment.tz(Signage.localTimeZone).format('ha'),
                                date: utcMoment.tz(Signage.localTimeZone).format('MM/DD'),
                            };
                        }),
                    },
                });
            },
            () => {
                Log.Warning(
                    `Failed to retrieve Weather (locality=${this.widget.config.zip})`,
                    null,
                    __fileline
                );
            }
        );
    }

    schedulerAction(): void {
        if (!this.lastUpdate || this.lastUpdate < Date.now() - 1800000) {
            //has not updated from Mercury as expected.
            if (isNullish(this.lastUpdate)) {
                Log.Warning(
                    `Weather widget is stale (${this.id}:${this.name}); will update from API`,
                    null,
                    __fileline
                );
            }

            this._updateComponentWithApiData();
        }
    }

    /**
     * Returns layout type
     */
    getLayoutType(): string {
        if (this.wrapperElement.clientHeight > this.wrapperElement.clientWidth) {
            return WeatherLayoutType.PORTRAIT;
        }

        return WeatherLayoutType.LANDSCAPE;
    }

    /**
     * Uses linear equation to find the minimum possible font size.
     * @returns fontsize - Minimum font size for the widget text
     */
    getMinimumFontSize(): number {
        let fontsize = 8;
        if (this.config.width && this.config.height) {
            if (this.getLayoutType() === WeatherLayoutType.PORTRAIT) {
                fontsize = Math.round(
                    ((parseInt(this.config.width) + 209) / 54) *
                        (parseInt(this.config.height) / parseInt(this.config.width))
                );
            } else {
                fontsize = Math.round((parseInt(this.config.width) + 209) / 54);
            }
        }

        return fontsize;
    }

    /**
     * Uses linear equation to find the maximum possible font size. y = 26x + 24,
     * where, y is screen width and x is font-size, might need improving
     * @returns Maximum font size for the widget text
     */
    getMaximumFontSize(): number {
        let fontsize = 8;
        //the equation of the trend line y = 26x + 24, where, y is screen width and
        //x is font-size
        if (this.config.width && this.config.height) {
            if (this.getLayoutType() === WeatherLayoutType.PORTRAIT) {
                fontsize =
                    Math.round(
                        ((parseInt(this.config.width) - 24) / 26 + 0.00001) *
                            (parseInt(this.config.height) / parseInt(this.config.width))
                    ) + 4;
            } else {
                fontsize = Math.round((parseInt(this.config.width) - 24) / 26 + 0.00001) + 4;
            }
        }

        return fontsize;
    }
}
